/**
 * Auth utilities for token management and authentication
 */

// Store token in localStorage for persistence
const TOKEN_KEY = 'bynn_auth_token';

/**
 * Get the auth token from storage
 * @returns JWT token string or null if not available
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Save the auth token to storage
 * @param token JWT token string
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Remove the auth token from storage
 */
export const removeAuthToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Check if user is authenticated
 * @returns boolean indicating if valid token exists
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  
  if (!token) {
    return false;
  }
  
  // Simple validation for token expiration - in production you'd want better validation
  try {
    const payload = token.split('.')[1];
    const decodedPayload = JSON.parse(atob(payload));
    
    // Check if token has expired
    if (decodedPayload.exp && decodedPayload.exp * 1000 < Date.now()) {
      removeAuthToken();
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    removeAuthToken();
    return false;
  }
};

/**
 * Parse the JWT token for user info
 * @returns Decoded token payload or null if invalid
 */
export const getTokenInfo = (): any | null => {
  const token = getAuthToken();
  
  if (!token) {
    return null;
  }
  
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch (error) {
    console.error('Error parsing token:', error);
    return null;
  }
};

/**
 * Extract verification ID from token
 * This assumes your JWT contains a 'verificationId' claim
 */
export const getVerificationId = (): string | null => {
  const tokenInfo = getTokenInfo();
  return tokenInfo && tokenInfo.verificationId ? tokenInfo.verificationId : null;
};